import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';

const MAINTENANCE_COLLECTION = 'maintenanceRequests';

export const maintenanceService = {
  // Generate unique maintenance request ID
  generateRequestId() {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ0123456789'; // Excluding I, O
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return `MR-${result}`;
  },

  // Check if request ID already exists
  async isRequestIdUnique(requestId) {
    try {
      const q = query(
        collection(db, MAINTENANCE_COLLECTION),
        where('requestId', '==', requestId)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.empty;
    } catch (error) {
      console.error('Error checking request ID uniqueness:', error);
      return false;
    }
  },

  // Generate unique request ID
  async generateUniqueRequestId() {
    let requestId;
    let attempts = 0;
    const maxAttempts = 100;

    do {
      requestId = this.generateRequestId();
      attempts++;
      
      if (attempts > maxAttempts) {
        throw new Error('Unable to generate unique request ID after maximum attempts');
      }
    } while (!(await this.isRequestIdUnique(requestId)));

    return requestId;
  },

  // Create a new maintenance request
  async createMaintenanceRequest(requestData) {
    try {
      // Generate unique request ID
      const requestId = await this.generateUniqueRequestId();
      
      const request = {
        ...requestData,
        requestId,
        status: 'pending',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const docRef = await addDoc(collection(db, MAINTENANCE_COLLECTION), request);
      return { id: docRef.id, ...request };
    } catch (error) {
      console.error('Error creating maintenance request:', error);
      throw error;
    }
  },

  // Get all maintenance requests for a company
  async getMaintenanceRequestsByCompany(companyId) {
    try {
      const q = query(
        collection(db, MAINTENANCE_COLLECTION),
        where('companyId', '==', companyId)
      );
      const querySnapshot = await getDocs(q);
      const requests = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort in JavaScript instead of Firestore to avoid index requirement
      return requests.sort((a, b) => b.createdAt?.toDate?.() - a.createdAt?.toDate?.());
    } catch (error) {
      console.error('Error getting maintenance requests:', error);
      throw error;
    }
  },

  // Get maintenance requests by tenant
  async getMaintenanceRequestsByTenant(tenantId) {
    try {
      const q = query(
        collection(db, MAINTENANCE_COLLECTION),
        where('tenantId', '==', tenantId)
      );
      const querySnapshot = await getDocs(q);
      const requests = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort in JavaScript instead of Firestore to avoid index requirement
      return requests.sort((a, b) => b.createdAt?.toDate?.() - a.createdAt?.toDate?.());
    } catch (error) {
      console.error('Error getting maintenance requests by tenant:', error);
      throw error;
    }
  },

  // Get maintenance request by ID
  async getMaintenanceRequestById(requestId) {
    try {
      const docRef = doc(db, MAINTENANCE_COLLECTION, requestId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error('Maintenance request not found');
      }
    } catch (error) {
      console.error('Error getting maintenance request:', error);
      throw error;
    }
  },

  // Update maintenance request
  async updateMaintenanceRequest(requestId, updateData) {
    try {
      const docRef = doc(db, MAINTENANCE_COLLECTION, requestId);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: new Date()
      });
      return { id: requestId, ...updateData };
    } catch (error) {
      console.error('Error updating maintenance request:', error);
      throw error;
    }
  },

  // Update maintenance request status
  async updateMaintenanceRequestStatus(requestId, status, managerNotes = '') {
    try {
      const docRef = doc(db, MAINTENANCE_COLLECTION, requestId);
      await updateDoc(docRef, {
        status,
        managerNotes,
        updatedAt: new Date()
      });
      return { id: requestId, status, managerNotes };
    } catch (error) {
      console.error('Error updating maintenance request status:', error);
      throw error;
    }
  },

  // Delete maintenance request
  async deleteMaintenanceRequest(requestId) {
    try {
      const docRef = doc(db, MAINTENANCE_COLLECTION, requestId);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      console.error('Error deleting maintenance request:', error);
      throw error;
    }
  },

  // Get maintenance requests by status
  async getMaintenanceRequestsByStatus(companyId, status) {
    try {
      const q = query(
        collection(db, MAINTENANCE_COLLECTION),
        where('companyId', '==', companyId),
        where('status', '==', status)
      );
      const querySnapshot = await getDocs(q);
      const requests = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort in JavaScript instead of Firestore to avoid index requirement
      return requests.sort((a, b) => b.createdAt?.toDate?.() - a.createdAt?.toDate?.());
    } catch (error) {
      console.error('Error getting maintenance requests by status:', error);
      throw error;
    }
  },

  // Get maintenance statistics for dashboard
  async getMaintenanceStats(companyId) {
    try {
      const allRequests = await this.getMaintenanceRequestsByCompany(companyId);
      
      const stats = {
        total: allRequests.length,
        pending: allRequests.filter(r => r.status === 'pending').length,
        inProgress: allRequests.filter(r => r.status === 'in_progress').length,
        completed: allRequests.filter(r => r.status === 'completed').length,
        cancelled: allRequests.filter(r => r.status === 'cancelled').length
      };

      return stats;
    } catch (error) {
      console.error('Error getting maintenance stats:', error);
      throw error;
    }
  }
}; 